import { useCallback, useEffect, useRef } from "react";
import { TransactionCategory, TransactionCategoryDetail, TransactionCategoryGroup, TransactionDetailValue, TransactionCustomFieldValues } from "../../../types";
import { TransactionWithDate } from "../../../utils/transactionProcessing";

interface FormData {
  title: string;
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string;
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: TransactionDetailValue;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

interface UseCategoryDetailsProps {
  dialogType: "expense" | "income";
  dialogOpen: boolean;
  transferDialogOpen: boolean;
  formData: FormData;
  /** When set, we're editing; sync runs once to fill any category detail fields missing from the transaction (e.g. new details added to category). */
  editingTransaction: TransactionWithDate | null;
  expenseCategories: TransactionCategory[];
  incomeCategories: TransactionCategory[];
  categoryGroups: TransactionCategoryGroup[];
  transactions: TransactionWithDate[];
  selectedCategoryDetails: TransactionCategoryDetail[];
  newlyAddedDetailOptions: Record<string, string[]>;
  detailInputValues: Record<string, string>;
  setSelectedCategoryDetails: (details: TransactionCategoryDetail[]) => void;
  setFormData: (data: FormData | ((prev: FormData) => FormData)) => void;
  setDetailInputValues: (values: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)) => void;
  setNewlyAddedDetailOptions: (options: Record<string, string[]> | ((prev: Record<string, string[]>) => Record<string, string[]>)) => void;
  setOpenDetailDropdowns: (dropdowns: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
}

export const useCategoryDetails = ({
  dialogType,
  dialogOpen,
  transferDialogOpen,
  formData,
  editingTransaction,
  expenseCategories,
  incomeCategories,
  categoryGroups,
  transactions,
  selectedCategoryDetails,
  newlyAddedDetailOptions,
  detailInputValues,
  setSelectedCategoryDetails,
  setFormData,
  setDetailInputValues,
  setNewlyAddedDetailOptions,
  setOpenDetailDropdowns,
}: UseCategoryDetailsProps) => {
  const hasSyncedDetailsForEditRef = useRef(false);

  // Helper function to derive category details from selected category
  const deriveCategoryDetails = useCallback((categoryName: string, preserveExistingValues: boolean = false) => {
    try {
      if (!categoryName || categoryName.trim() === "") {
        setSelectedCategoryDetails([]);
        return;
      }

      const categories = dialogType === "expense" ? expenseCategories : incomeCategories;
      const selectedCategory = categories.find(cat => cat.name === categoryName);
      
      if (!selectedCategory) {
        setSelectedCategoryDetails([]);
        return;
      }

      // Get details from the category's group
      const group = selectedCategory?.groupId ? categoryGroups.find((g) => g.id === selectedCategory.groupId) : null;
      const allGroupDetails = group?.details || [];
      
      // Only show details that are explicitly enabled for this category
      const enabledDetailNames = Array.isArray(selectedCategory?.enabledDetails) 
        ? selectedCategory.enabledDetails 
        : [];
      
      const details = enabledDetailNames.length > 0 
        ? allGroupDetails.filter(detail => enabledDetailNames.includes(detail.name))
        : [];
      
      setSelectedCategoryDetails(details);

      // Initialize details with default values from category settings or previous transactions
      // Compute detailsValue first so we can use it for both formData and inputValues
      let detailsValue: TransactionDetailValue | undefined = undefined;
      if (details.length > 0) {
        // Get current formData.details for preserving values
        const currentDetails = formData.details;
        detailsValue = preserveExistingValues && currentDetails ? { ...currentDetails } : {};
        
        // Use default values from category settings
        // IMPORTANT: Always use the latest category data from the categories array (updated by real-time listeners)
        const categoryDefaults = (selectedCategory?.defaultValues && typeof selectedCategory.defaultValues === 'object' && !Array.isArray(selectedCategory.defaultValues))
          ? selectedCategory.defaultValues 
          : {};
        
        for (const detail of details) {
          // If preserving existing values and this detail already has a value, keep it
          if (preserveExistingValues && currentDetails && detail.name in currentDetails && currentDetails[detail.name] !== undefined) {
            detailsValue[detail.name] = currentDetails[detail.name];
            continue;
          }
        
          const hasDefault = detail.name in categoryDefaults;
          const defaultVal = hasDefault ? categoryDefaults[detail.name] : undefined;
          
          // Priority 1: Use default value from category settings
          if (hasDefault && defaultVal !== "" && defaultVal !== null && defaultVal !== undefined) {
            if (detail.type === "list" && detail.listItems && Array.isArray(detail.listItems)) {
              const defaultValStr = String(defaultVal);
              // Always apply the default value, even if it's not in the list items
              // The Autocomplete component can handle values not in the list (freeSolo mode)
              detailsValue[detail.name] = defaultValStr;
            } else if (detail.type === "measurable") {
              const numVal = typeof defaultVal === 'number' ? defaultVal : (typeof defaultVal === 'string' ? parseFloat(defaultVal) : 0);
              if (!isNaN(numVal)) {
                detailsValue[detail.name] = numVal;
              } else {
                detailsValue[detail.name] = "";
              }
            } else {
              detailsValue[detail.name] = String(defaultVal);
            }
          } else if (detail.type === "measurable") {
            // For measurable, try to get last value from previous transactions
            const previousTransactions = transactions.filter(
              (t) => t.category === categoryName && t.details && detail.name in t.details
            );
            if (previousTransactions.length > 0 && previousTransactions[0].details) {
              const lastValue = previousTransactions[0].details[detail.name];
              if (lastValue !== undefined) {
                detailsValue[detail.name] = lastValue;
              } else {
                detailsValue[detail.name] = "";
              }
            } else {
              detailsValue[detail.name] = "";
            }
          } else if (detail.type === "list") {
            // For list types, try to get last value from previous transactions if it exists in current list items
            const previousTransactions = transactions.filter(
              (t) => t.category === categoryName && t.details && detail.name in t.details
            );
            if (previousTransactions.length > 0 && previousTransactions[0].details && detail.listItems && Array.isArray(detail.listItems)) {
              const lastValue = previousTransactions[0].details[detail.name];
              const lastValueStr = typeof lastValue === "string" ? lastValue : String(lastValue);
              // Only use the value if it exists in the current list items
              if (lastValue !== undefined && detail.listItems.includes(lastValueStr)) {
                detailsValue[detail.name] = lastValueStr;
              } else {
                detailsValue[detail.name] = "";
              }
            } else {
              detailsValue[detail.name] = "";
            }
          } else {
            detailsValue[detail.name] = "";
          }
        }
      } else {
        detailsValue = undefined;
      }

      // Always update form data with new details
      // When preserving values, detailsValue already contains merged existing + default values
      // Always update if preserveExistingValues is false (manual category selection)
      // Or if form has no details yet (new transaction)
      if (!preserveExistingValues || !formData.details || Object.keys(formData.details || {}).length === 0) {
        setFormData(prevFormData => {
          const currentDetails = prevFormData.details;
          const detailsChanged = JSON.stringify(currentDetails) !== JSON.stringify(detailsValue);
          if (!detailsChanged && currentDetails !== undefined) {
            return prevFormData;
          }
          return {
            ...prevFormData,
            details: detailsValue,
          };
        });
      }
      
      // Also sync inputValues for list-type details so Autocomplete displays correctly
      // This ensures the Autocomplete inputValue matches the formData.details value
      if (details.length > 0 && detailsValue) {
        const newInputValues: Record<string, string> = {};
        for (const detail of details) {
          if (detail.type === "list" && detail.name in detailsValue) {
            const detailValue = detailsValue[detail.name];
            if (detailValue !== undefined && detailValue !== null && detailValue !== "") {
              newInputValues[detail.name] = String(detailValue);
            }
          }
        }
        
        // Update input values for list-type details
        if (Object.keys(newInputValues).length > 0) {
          setDetailInputValues(prev => ({
            ...prev,
            ...newInputValues,
          }));
        }
      }
    } catch (error) {
      console.error("❌ ERROR in deriveCategoryDetails:", error);
    }
  }, [dialogType, expenseCategories, incomeCategories, categoryGroups, transactions, formData.details, setSelectedCategoryDetails, setFormData, setDetailInputValues]);

  // Reset "synced for edit" when we're not editing
  useEffect(() => {
    if (!editingTransaction) {
      hasSyncedDetailsForEditRef.current = false;
    }
  }, [editingTransaction]);

  // Sync category details when categories or groups change (not when category changes)
  // This ensures details are properly loaded even if data loads after category selection
  // When category is manually selected, onChange calls deriveCategoryDetails with preserveExistingValues=false
  // When data loads, this effect calls it with preserveExistingValues=true to preserve user-entered values
  // When editing, run once with preserve=true so any new detail fields (added to category after transaction) get defaults
  useEffect(() => {
    // Only sync if a category is already selected and expense/income dialog is open (not transfer dialog)
    if (dialogOpen && !transferDialogOpen && formData.category && formData.category.trim() !== "") {
      // Check if categories and groups are loaded (not empty arrays)
      const hasCategories = (dialogType === "expense" ? expenseCategories : incomeCategories).length > 0;
      const hasGroups = categoryGroups.length > 0;
      
      if (hasCategories && hasGroups) {
        const categories = dialogType === "expense" ? expenseCategories : incomeCategories;
        const selectedCategory = categories.find(cat => cat.name === formData.category);
        if (selectedCategory) {
          const group = selectedCategory?.groupId ? categoryGroups.find((g) => g.id === selectedCategory.groupId) : null;
          const allGroupDetails = group?.details || [];
          const enabledDetailNames = Array.isArray(selectedCategory?.enabledDetails) ? selectedCategory.enabledDetails : [];
          const expectedDetails = enabledDetailNames.length > 0 
            ? allGroupDetails.filter(detail => enabledDetailNames.includes(detail.name))
            : [];
          
          const expectedCount = expectedDetails.length;
          const currentCount = selectedCategoryDetails.length;
          const isFormEmpty = !formData.details || Object.keys(formData.details).length === 0;
          const isEditingThisCategory = !!editingTransaction && editingTransaction.category === formData.category;

          // When editing this transaction, run deriveCategoryDetails once with preserve=true so any
          // detail fields that exist for the category but were missing from the transaction get defaults
          if (isEditingThisCategory && !hasSyncedDetailsForEditRef.current) {
            hasSyncedDetailsForEditRef.current = true;
            deriveCategoryDetails(formData.category, true);
            return;
          }

          // Only sync if the details list changed or form is empty (new transaction).
          // Do NOT sync just because form value !== category default — that means the user edited the value.
          const shouldSync = currentCount !== expectedCount || currentCount === 0 || isFormEmpty;
          const shouldPreserve = !isFormEmpty;

          if (shouldSync) {
            deriveCategoryDetails(formData.category, shouldPreserve);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryGroups, expenseCategories, incomeCategories, dialogType, dialogOpen, transferDialogOpen, editingTransaction, deriveCategoryDetails]);

  // Handle creating new detail option from Autocomplete input
  const handleCreateDetailOption = useCallback((detailName: string, inputValue: string) => {
    if (!inputValue.trim()) {
      return;
    }

    const newOption = inputValue.trim();
    
    // Check if option already exists (in original listItems or newly added)
    const detail = selectedCategoryDetails.find(d => d.name === detailName);
    if (detail && detail.listItems && detail.listItems.includes(newOption)) {
      // Option already exists, just select it
      setFormData({
        ...formData,
        details: {
          ...(formData.details || {}),
          [detailName]: newOption,
        },
      });
      setDetailInputValues({
        ...detailInputValues,
        [detailName]: "",
      });
      return;
    }
    
    const existingNewOptions = newlyAddedDetailOptions[detailName] || [];
    if (existingNewOptions.includes(newOption)) {
      // Already added, just select it
      setFormData({
        ...formData,
        details: {
          ...(formData.details || {}),
          [detailName]: newOption,
        },
      });
      setDetailInputValues({
        ...detailInputValues,
        [detailName]: "",
      });
      return;
    }

    // Add the new option
    const updatedOptions = [...existingNewOptions, newOption];
    setNewlyAddedDetailOptions({
      ...newlyAddedDetailOptions,
      [detailName]: updatedOptions,
    });

    // Auto-select the newly added option
    setFormData({
      ...formData,
      details: {
        ...(formData.details || {}),
        [detailName]: newOption,
      },
    });

    // Clear input
    setDetailInputValues({
      ...detailInputValues,
      [detailName]: "",
    });

    // Close the dropdown after creating and selecting
    setOpenDetailDropdowns(prev => {
      const newSet = new Set(prev);
      newSet.delete(detailName);
      return newSet;
    });
  }, [selectedCategoryDetails, newlyAddedDetailOptions, detailInputValues, formData, setFormData, setDetailInputValues, setNewlyAddedDetailOptions, setOpenDetailDropdowns]);

  return {
    deriveCategoryDetails,
    handleCreateDetailOption,
  };
};
